/**
 * Client-side helper that syncs an authenticated user's analytics_storage
 * consent choice to the backend so server-side GA4 Measurement Protocol
 * event gating stays in step with what the user sees in the banner.
 *
 * Fire-and-forget semantics: UI callers should NOT await this promise on
 * their critical path. The banner persists the cookie (the source of truth
 * for client-side gtag signals) synchronously; this relay is only a
 * best-effort server update.
 *
 * Anonymous visitors: skip. There is no user document to attribute consent
 * to, and no server-side GA4 event can fire for an anonymous user anyway.
 * The consent state lives in the `nrs_ga_consent` cookie and is forwarded
 * to Composer at signup time as part of the create-user request body.
 */

import type { StoredConsentChoice } from "./consent";
import { readStoredConsent } from "./consent";
import { sanitizeGaClientId } from "../google/ga-cookie";

const CONSENT_ENDPOINT = "/api/analytics/consent";

/**
 * POSTs the user's analytics_storage choice to the local relay route,
 * which forwards to Composer. Swallows transport / HTTP errors: a failed
 * relay must not block banner UX or trigger visible errors.
 */
export async function relayAnalyticsConsent(
  choice: StoredConsentChoice,
  accessToken: string | null | undefined,
  gaClientId?: string | null
): Promise<boolean> {
  if (!accessToken || typeof accessToken !== "string") return false;
  if (typeof fetch === "undefined") return false;
  const sanitizedClientId =
    choice === "granted" ? sanitizeGaClientId(gaClientId) : null;
  try {
    const response = await fetch(CONSENT_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
      },
      body: JSON.stringify({
        choice,
        ...(sanitizedClientId ? { ga_client_id: sanitizedClientId } : {}),
      }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    /* @architecture:acceptable - best-effort consent relay */
    return false;
  }
}

/**
 * Relays whatever consent cookie exists. Used after Firebase login so
 * Composer receives ``analytics_consent_granted`` even when ``_ga`` is
 * not ready yet. Returns false when there is no stored decision or token.
 */
export async function relayStoredAnalyticsConsent(
  accessToken: string | null | undefined,
  gaClientId?: string | null
): Promise<boolean> {
  const stored = readStoredConsent();
  if (!stored) return false;
  return relayAnalyticsConsent(
    stored.analytics_storage,
    accessToken,
    gaClientId
  );
}
